import type { NorskPensjonPkb } from "~/flytt-pkb/felles/hooks/useFlyttPkb";
import actions from "~/shared/actions";

interface Balance {
	upperLimit?: number;
	expected?: number;
	lowerLimit?: number;
}

interface Basis {
	age?: number;
	savingAmount?: number;
	insuranceCost?: number;
	adminCost?: number;
	managementCost?: number;
	managementCostPercent?: number;
	gValue?: number;
}

export interface Details {
	arbeidsgiver?: string;
	produktBetegnelse?: string;
	yearlyDisbursement: number;
	isSB1: boolean;
	selskapsNavn?: string;
	prognosis?: {
		saldoReell?: Balance;
		saldoNaverdi?: Balance;
		basis?: Basis;
		utbetaling?: Balance;
		taxReimbursementPerYear: number;
		prognosisAge: number;
		avkastningAksjerPros: number;
		avkastningBankkontoPros: number;
		inflasjonPros: number;
	} | null;
	sisteUtbetaling?: number;
	flyttbar: boolean;
	startAlder: number;
	sluttAlder: number;
	utbetalingsperioder: UtbetalingsPeriode[];
}

interface UtbetalingsPeriode {
	startMaaned: number;
	sluttMaaned: number;
	grad: number;
	aarligUtbetalingForventet: number;
	aarligUtbetalingNedreGrense: number;
	aarligUtbetalingOvreGrense: number;
}

type Category = "CURRENT_EMPLOYERS" | "FOLKETRYGD" | "FORMER_EMPLOYERS" | "SAVINGS";

type NorskPensjonRettighet<T extends Category> = {
	category: T;
	yearlyDisbursement: number;
	details: Details[];
};

export interface NorskPensjon {
	currentEmployers: NorskPensjonRettighet<"CURRENT_EMPLOYERS">;
	folketrygd: NorskPensjonRettighet<"FOLKETRYGD">;
	formerEmployers: NorskPensjonRettighet<"FORMER_EMPLOYERS">;
	savings: NorskPensjonRettighet<"SAVINGS">;
	customerAge: number;
	startAge: number;
	pkbs: NorskPensjonPkb[];
	unavailable: boolean;
	unavailableInstruments: [];
	utilgjengeligeInnretninger?: string[];
	isFetched: boolean;
	isFetching: boolean;
	extraSavings: number;
	yearlySalary: number;
}

function requestNorskPensjon(state, { data: { startAge, yearlySalary } }) {
	return {
		...state,
		isFetching: true,
		startAge,
		yearlySalary,
	};
}

function receiveNorskPensjon(state, { data }) {
	const { currentEmployers, errorCode, folketrygd, formerEmployers, hasOffentligPensjon, savings, utilgjengeligeInnretninger, startAlder, sluttAlder, utbetalingsperioder } = data;

	if (errorCode) {
		return {
			...state,
			errorCode,
			isFetched: true,
			isFetching: false,
		};
	}

	return {
		...state,
		isFetched: true,
		isFetching: false,
		currentEmployers,
		folketrygd,
		formerEmployers,
		hasOffentligPensjon,
		savings,
		utilgjengeligeInnretninger,
		startAlder,
		sluttAlder,
		utbetalingsperioder,
	};
}

function norskPensjonReceiveCustomer(state, { data }) {
	return {
		...state,
		customerAge: data.age,
	};
}

function receivePkbs(state, action) {
	return {
		...state,
		pkbs: action.data.pkbs,
		unavailable: action.data.unavailable,
		unavailableInstruments: action.data.unavailableInstruments,
	};
}

export const defaultState: NorskPensjon = {
	currentEmployers: {
		category: "CURRENT_EMPLOYERS",
		yearlyDisbursement: 0,
		details: [],
	},
	folketrygd: {
		category: "FOLKETRYGD",
		yearlyDisbursement: 0,
		details: [],
	},
	formerEmployers: {
		category: "FORMER_EMPLOYERS",
		yearlyDisbursement: 0,
		details: [],
	},
	savings: {
		category: "SAVINGS",
		yearlyDisbursement: 0,
		details: [],
	},
	customerAge: 18,
	extraSavings: 300,
	isFetched: false,
	isFetching: false,
	startAge: 67,
	yearlySalary: 0,
	pkbs: [],
	unavailable: false,
	unavailableInstruments: [],
	utilgjengeligeInnretninger: [],
};

function resetNorskPensjon(state) {
	return {
		...defaultState,
		customerAge: state.customerAge,
	};
}

export function getInitialState({ age }) {
	return {
		...defaultState,
		customerAge: age,
	};
}

export default function norskpensjon(state = defaultState, action) {
	switch (action.type) {
		case actions.NORSK_PENSJON_REQUEST:
			return requestNorskPensjon(state, action);
		case actions.NORSK_PENSJON_RESPONSE:
			return receiveNorskPensjon(state, action);
		case actions.RESET_NORSK_PENSJON:
			return resetNorskPensjon(state);
		case actions.CUSTOMER_RESPONSE:
			return norskPensjonReceiveCustomer(state, action);
		case actions.NORSK_PENSJON_PKBS_RESPONSE:
			return receivePkbs(state, action);
		case actions.SET_EXTRA_SAVINGS:
			return {
				...state,
				extraSavings: action.data,
			};
		default:
			return state;
	}
}
